import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ArrowUpRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Layout from '../components/Layout';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';

const Billing = () => {
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBillingInfo();
    fetchCreditHistory();
  }, []);

  const fetchBillingInfo = async () => {
    try {
      const response = await apiClient.get('/billing/status');
      setBillingInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch billing info:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await apiClient.get('/billing/credit-audit-log');
      setCreditHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch credit history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const returnUrl = window.location.origin + '/billing';
      const response = await apiClient.post('/billing/create-portal-session', {
        return_url: returnUrl,
      });

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'free':
        return 'bg-gray-100 text-gray-800';
      case 'starter':
        return 'bg-blue-100 text-blue-800';
      case 'pro':
        return 'bg-teal-100 text-teal-800';
      case 'org':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trial':
        return 'bg-yellow-100 text-yellow-800';
      case 'past_due':
        return 'bg-red-100 text-red-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading billing information...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Billing & Subscription</h1>
          <p className="text-muted-foreground mt-2">
            Manage your plan, credits, and payment information
          </p>
        </div>

        <div className="grid gap-6">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>
              Your active subscription and billing details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Plan</p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      getPlanBadgeColor(billingInfo?.plan_type)
                    }`}
                  >
                    {billingInfo?.plan_type?.toUpperCase() || 'FREE'}
                  </span>
                  {billingInfo?.billing_cycle && (
                    <span className="text-sm text-muted-foreground">
                      ({billingInfo.billing_cycle})
                    </span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mt-1 ${
                    getStatusBadgeColor(billingInfo?.subscription_status)
                  }`}
                >
                  {billingInfo?.subscription_status?.toUpperCase() || 'TRIAL'}
                </span>
              </div>
            </div>

            {billingInfo?.plan_type !== 'free' && (
              <div className="flex gap-4 pt-4 border-t">
                <Button
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                  className="flex-1"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {portalLoading ? 'Loading...' : 'Manage Billing'}
                </Button>
              </div>
            )}

            {billingInfo?.plan_type === 'free' && (
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full"
              >
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Upgrade Plan
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Credits */}
        <Card>
          <CardHeader>
            <CardTitle>Credits</CardTitle>
            <CardDescription>
              Your available credits for AI-powered analysis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Credits Remaining</p>
                  <p className="text-3xl font-bold mt-1">
                    {billingInfo?.credits_remaining || 0}
                  </p>
                </div>
                {billingInfo?.next_refill_date && (
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Next Refill</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm font-medium">
                        {formatDate(billingInfo.next_refill_date)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-medium mb-2">Credit Costs:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Standard Ask: 1 credit</li>
                  <li>• Case Review: 5 credits (Pro/Org only)</li>
                  <li>• PDF Export: Free</li>
                </ul>
              </div>

              {billingInfo?.credits_remaining === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ Out of credits
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Upgrade your plan to get more credits and continue using CBAIQ.
                  </p>
                  <Button
                    onClick={() => navigate('/pricing')}
                    variant="outline"
                    size="sm"
                    className="mt-3"
                  >
                    View Plans
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Credit Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle>Credit Transaction History</CardTitle>
            <CardDescription>
              Complete log of all credit transactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            ) : creditHistory.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No credit transactions yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your credit usage will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Table Header */}
                <div className="grid grid-cols-4 gap-4 pb-2 border-b text-sm font-medium text-muted-foreground">
                  <div>Date & Time</div>
                  <div>Action</div>
                  <div className="text-right">Amount</div>
                  <div className="text-right">Balance After</div>
                </div>

                {/* Transaction Rows */}
                {creditHistory.map((transaction, idx) => {
                  const isDeduction = transaction.amount_deducted > 0;
                  const isGrant = transaction.amount_deducted < 0;
                  
                  return (
                    <div
                      key={idx}
                      className="grid grid-cols-4 gap-4 py-3 border-b last:border-0 hover:bg-muted/50 transition"
                    >
                      {/* Date */}
                      <div className="text-sm">
                        <p className="font-medium">
                          {new Date(transaction.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(transaction.timestamp).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>

                      {/* Reason */}
                      <div className="text-sm">
                        <p className="font-medium">{transaction.reason}</p>
                      </div>

                      {/* Amount */}
                      <div className="text-right">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded text-sm font-semibold ${
                            isGrant
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {isGrant ? '+' : '-'}
                          {Math.abs(transaction.amount_deducted)}
                        </span>
                      </div>

                      {/* Balance After */}
                      <div className="text-right">
                        <p className="text-sm font-medium">{transaction.credits_after}</p>
                        <p className="text-xs text-muted-foreground">remaining</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Billing;
